import type { SourceAnchor } from '@/services/ingestion'
import type {
  DataReviewDecisionResult,
  DataReviewItem,
  DataReviewSettled,
  DataReviewSource,
} from '@/services/dataReview'
import { show, sourceAnchor } from './reparseFormat'
import { STATUS_META, anchorFor } from './datapointFormat'
import { formatSample } from './discoveryFormat'

/**
 * How a row of the Data Intelligence Review reads on screen, whichever of the
 * old screens it came from. Pure data and pure functions, kept apart from the
 * components so a component file exports components and nothing else.
 */

export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/**
 * What the Intake tab's badge counts, in words. The badge is the decisions a
 * person owes on deal data across the whole desk - a conflict to settle, a value
 * to confirm, an identity to support - while the review panel beside it counts
 * one record or one deal; a bare number on each reads as two answers to one
 * question. Found datapoints are a vocabulary list: they are named beside the
 * badge, not in it.
 */
export function describeDesk(
  totals: { all: number; open: number; deals: number; discovery: number; recordsOwed?: number },
): string {
  const records = totals.recordsOwed ?? totals.deals
  const found = totals.discovery > 0 ? `; ${plural(totals.discovery, 'found datapoint')} listed separately` : ''
  return `${plural(totals.all, 'decision')} owed on the desk — ${plural(totals.open, 'question')} on ${plural(records, 'record')}${found}`
}

/** The kinds of open row that are not a decision owed, in the words the review
 *  panel uses for them, in the order it lists them. Keyed as the map arrives —
 *  camel-cased by `toCamelCase` (`engineReread`, not the server's `engine_reread`). */
const SETTLED_LABEL: [keyof DataReviewSettled, string, string][] = [
  ['gap', 'value no document states', 'values no document states'],
  ['engineReread', 'engine re-read the nightly sweep applies', 'engine re-reads the nightly sweep applies'],
  ['informational', 'reading beside a value a person decided', 'readings beside values a person decided'],
  ['answered', 'note a person has since answered', 'notes a person has since answered'],
  ['duplicate', 'duplicate of an open checklist row', 'duplicates of open checklist rows'],
  ['elsewhere', 'task for the Underwriting page', 'tasks for the Underwriting page'],
  ['supportingMail', 'checklist row on supporting mail', 'checklist rows on supporting mail'],
  ['modelDetail', 'sponsor-model line listed with its readings', 'sponsor-model lines listed with their readings'],
]

/** What else is open on the desk and is not owed: "46 values no document states",
 *  "52 engine re-reads the nightly sweep applies", and so on. Empty when there is none. */
export function describeSettled(settled: Partial<DataReviewSettled> | undefined): string[] {
  if (!settled) return []
  return SETTLED_LABEL
    .filter(([key]) => (settled[key] ?? 0) > 0)
    .map(([key, one, many]) => plural(settled[key] ?? 0, one, many))
}

/**
 * Is this open row a decision a person owes — what the inbox's Open column and
 * the Intake badge count? One rule for every screen, so the panel a record opens
 * in cannot give it a different count from the row that opened it. A server that
 * files no bucket is read as it always was: every row of a record is a decision.
 * A found datapoint is never one — it is a vocabulary list, counted beside the
 * badge and never in it.
 */
export function isOwed(item: DataReviewItem): boolean {
  if (item.source === 'discovery' || item.kind === 'decided') return false
  return (item.bucket ?? 'decision') === 'decision'
}

export const SOURCE_LABEL: Record<DataReviewSource, string> = {
  review: 'Checklist',
  reparse: 'Re-parse',
  reconcile: 'Deal',
  discovery: 'Found',
}

type KindMeta = { label: string; cls: string; dot: string }

const SKY = 'text-sky-300 bg-sky-400/10 border-sky-400/25'
const AMBER = 'text-amber-300 bg-amber-400/10 border-amber-400/25'
const RED = 'text-red-300 bg-red-400/10 border-red-400/25'
const VIOLET = 'text-violet-300 bg-violet-400/10 border-violet-400/25'
const STONE = 'text-stone-300 bg-stone-500/10 border-stone-500/30'

/** The chip each kind of question wears. The checklist's own statuses keep the
 *  colours they have always had. */
export const KIND_META: Record<string, KindMeta> = {
  ...STATUS_META,
  decided: { ...STATUS_META.resolved },
  replacement: { label: 're-read', cls: SKY, dot: 'bg-sky-400' },
  unsupported: { label: 'unsupported', cls: AMBER, dot: 'bg-amber-400' },
  rival_counterparties: { label: 'rivals', cls: RED, dot: 'bg-red-400' },
  name_unconfirmed: { label: 'name unconfirmed', cls: AMBER, dot: 'bg-amber-400' },
  no_counterparty_stated: { label: 'none stated', cls: STONE, dot: 'bg-stone-500' },
  new_field: { label: 'new field', cls: VIOLET, dot: 'bg-violet-400' },
  new_word: { label: 'new word', cls: VIOLET, dot: 'bg-violet-400' },
}

export function kindMeta(kind: string): KindMeta {
  return KIND_META[kind] ?? {
    label: kind.replace(/_/g, ' '), cls: STONE, dot: 'bg-stone-500',
  }
}

/** The tab of the deal's dossier that answers a question this screen cannot. */
export function dossierTabFor(where: string): 'underwriting' | 'overview' {
  return /underwriting/i.test(where) ? 'underwriting' : 'overview'
}

/** The row's value, one way for every source. A value nobody stated reads
 *  "Not recorded" — never a blank that could be mistaken for a loading cell. */
export function valueText(item: DataReviewItem): string {
  if (item.source === 'discovery') return formatSample(item.value as number | string | null)
  return show(item.value, item.field ?? '')
}

/** The document and place the preview pane should open at for this row. */
export function previewTarget(item: DataReviewItem): { sha: string | null; anchor: SourceAnchor | null } {
  const review = item.detail?.reviewItem
  if (review) {
    const p = review.provenance ?? null
    return { sha: p?.doc?.sha256 ?? null, anchor: anchorFor(p) }
  }
  const proposal = item.detail?.proposal
  if (proposal) {
    const evidence = proposal.incomingEvidence?.availability === 'recorded'
      ? proposal.incomingEvidence
      : proposal.currentEvidence ?? null
    return { sha: evidence?.sourceSha256 ?? null, anchor: evidence ? sourceAnchor(evidence) : null }
  }
  return { sha: null, anchor: null }
}

const OUTCOME_TEXT: Record<string, string> = {
  confirmed: 'Confirmed as read',
  corrected: 'Corrected',
  located: 'Taken from the place you pointed at',
  unavailable: 'Recorded as unavailable',
  withdrawn: 'Decision withdrawn — the engine’s reading is back',
  kept: 'Kept the stored value',
  replaced: 'Replaced with the engine’s new reading',
  cleared: 'Cleared',
  field_created: 'Added as a new field',
  vocabulary_added: 'Taught the parser this word',
  dismissed: 'Dismissed — not a datapoint',
}

/** What a decision did, in a sentence — including whether it reached the deal
 *  and, when it did not, why. The screen says so rather than leaving a person
 *  to open the pipeline and look. */
export function describeResult(item: DataReviewItem, result: DataReviewDecisionResult): string {
  const outcome = OUTCOME_TEXT[result.outcome] ?? 'Recorded'
  const mirror = result.mirroredToLoan
  const tail = !mirror ? ''
    : mirror.mirrored ? ' Written to the deal.'
      : mirror.why ? ` The deal is unchanged — ${String(mirror.why).replace(/\.$/, '')}.` : ''
  return `${item.label}: ${outcome}.${tail}`
}
